<script setup lang="ts">
import { computed } from 'vue';
import { useNowTicker } from '@/composables/useNowTicker';
import { usePlayerCommandHistory } from '@/composables/api/useSchedulerSyncApi';
import { useLocationZonePlayerCommands } from '@/composables/api/useLocationZonePlayerInspectorApi';
import { secondsSince } from '@/utils/checkinStatus';
import {
    derivePlayerSyncState,
    hasQueuedContentDelivery,
    latestContentCommand,
    playerSyncDetailText,
    playerSyncShortLabel,
} from '@/utils/syncStatus';

/**
 * One player under an expanded zone in the Sync popover:
 * "19969 4 AM · checked in 9s ago". The id opens the player in the inspector.
 *
 * The player's command history and pending commands are fetched here, so they
 * load only for the players of a zone someone expanded.
 */
const props = defineProps<{
    player: Record<string, unknown>;
    contentChangedAt: string | null;
}>();

const emit = defineEmits<{ openPlayer: [locationZonePlayerId: number] }>();

const nowMs = useNowTicker();

const locationZonePlayerId = computed<number | null>(() =>
    typeof props.player.id === 'number' ? props.player.id : Number(props.player.id) || null
);

const historyQuery = usePlayerCommandHistory(locationZonePlayerId);
const pendingCommandsQuery = useLocationZonePlayerCommands(locationZonePlayerId);

const checkinSeconds = computed(() => {
    const lastCheckinAt = props.player.player_data_updated_at;
    return secondsSince(typeof lastCheckinAt === 'string' ? lastCheckinAt : null, nowMs.value);
});

const isLoading = computed(() => historyQuery.isLoading.value || pendingCommandsQuery.isLoading.value);
const isError = computed(() => historyQuery.isError.value || pendingCommandsQuery.isError.value);

const syncState = computed(() =>
    derivePlayerSyncState({
        checkinSeconds: checkinSeconds.value,
        hasQueuedContentDelivery: hasQueuedContentDelivery(pendingCommandsQuery.data.value?.queued ?? []),
        latestContentCommand: latestContentCommand(historyQuery.data.value ?? []),
        contentChangedAt: props.contentChangedAt,
        referencedContentMissing:
            typeof props.player.player_data_referenced_content_missing === 'number'
                ? props.player.player_data_referenced_content_missing
                : null,
    })
);

// Offline needs only the check-in, so it shows before the history arrives.
const isAwaitingCommands = computed(() => syncState.value.kind !== 'offline' && (isLoading.value || isError.value));

/** The zone column: the muted detail. */
const detailText = computed(() => {
    if (isAwaitingCommands.value) return isLoading.value ? 'loading…' : "couldn't load its commands";
    return playerSyncDetailText(syncState.value, checkinSeconds.value, nowMs.value);
});

/** The state column: only the short labels, so expanding never widens it. */
const shortLabel = computed(() => (isAwaitingCommands.value ? '' : playerSyncShortLabel(syncState.value)));
</script>

<template>
    <!-- A row of the popover's grid, laid out like a zone row: the detail in the zone
         column, the id in the ids column, a short label in the state column. Only the
         short labels go in the state column, so opening a zone never moves the ids. -->
    <div class="col-span-full grid grid-cols-subgrid items-baseline text-xs" data-testid="sync-player-line">
        <span
            class="col-start-2 min-w-0 truncate"
            :class="isError && isAwaitingCommands ? 'text-destructive-foreground' : 'text-muted-foreground'"
            :title="detailText"
            data-testid="sync-player-detail"
        >
            {{ detailText }}
        </span>
        <span class="tabular-nums">
            <button
                v-if="locationZonePlayerId !== null"
                type="button"
                class="text-foreground hover:text-primary font-medium hover:underline focus-visible:outline-none focus-visible:underline"
                @click="emit('openPlayer', locationZonePlayerId)"
            >
                {{ locationZonePlayerId }}
            </button>
        </span>
        <span
            class="pr-2.5 text-right font-semibold whitespace-nowrap"
            :class="syncState.kind === 'offline' ? 'text-destructive-foreground' : ''"
            data-testid="sync-player-state"
        >
            {{ shortLabel }}
        </span>
    </div>
</template>
